import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PERMISSIONS } from '@lms/shared-types';
import { usePermissions } from '@/hooks/usePermissions';
import { coursePlayHref, courseTabHref, studentActivityHref } from '@/lib/course-admin-navigation';

export interface CourseSectionItem {
  id: string;
  title: string;
  itemType?: string;
  type?: string;
  typeValue?: string;
  learningObjectId?: string | null;
  isVisible?: boolean;
  sortOrder?: number;
  hasActiveAttempt?: boolean;
  isAccessible?: boolean;
  accessMessage?: string | null;
  isCompleted?: boolean;
}

export interface CourseSection {
  id: string;
  title: string;
  isVisible?: boolean;
  items?: CourseSectionItem[];
}

interface CourseContentModulesProps {
  courseId: string;
  sections: CourseSection[];
  adminContext?: boolean;
  canEditBuilder?: boolean;
}

function isLabelItem(item: CourseSectionItem) {
  return item.itemType === 'label';
}

function activityIcon(typeValue?: string) {
  switch (typeValue) {
    case 'h5p':
      return 'extension';
    case 'assessment':
      return 'quiz';
    case 'video':
      return 'play_circle';
    case 'pdf':
      return 'picture_as_pdf';
    case 'pdf_folder':
      return 'folder';
    case 'audio':
      return 'audio_file';
    case 'image':
      return 'image';
    case 'external_link':
      return 'link';
    case 'download':
      return 'download';
    default:
      return 'article';
  }
}

function activityTypeLabel(item: CourseSectionItem) {
  if (item.type) return item.type;
  if (item.itemType === 'label') return 'Etiqueta';
  return 'Actividad';
}

function activityTypeValue(item: CourseSectionItem) {
  return item.typeValue ?? item.itemType;
}

function itemHref(
  courseId: string,
  item: CourseSectionItem,
  adminContext: boolean,
  searchParams: URLSearchParams,
) {
  if (isLabelItem(item)) return null;
  if (adminContext) {
    return coursePlayHref(courseId, item.id, searchParams);
  }
  return studentActivityHref(courseId, item.id, searchParams);
}

function renderItemStatus(item: CourseSectionItem, showProgress: boolean) {
  if (!showProgress || isLabelItem(item)) return null;

  if (item.isCompleted) {
    return (
      <div className="lms-course-module-item__status" title="Completada">
        <span className="lms-course-module-item__check" aria-hidden="true">
          <span className="material-symbols-outlined text-base">check</span>
        </span>
        <span className="sr-only">Completada</span>
      </div>
    );
  }

  if (item.isAccessible === false) return null;

  return (
    <div className="lms-course-module-item__status" title="Pendiente">
      <span className="lms-course-module-item__pending" aria-hidden="true">
        <span className="material-symbols-outlined text-base">radio_button_unchecked</span>
      </span>
      <span className="sr-only">Pendiente</span>
    </div>
  );
}

export function CourseContentModules({
  courseId,
  sections,
  adminContext = true,
  canEditBuilder,
}: CourseContentModulesProps) {
  const [searchParams] = useSearchParams();
  const { hasPermission } = usePermissions();
  const allowEditBuilder = (canEditBuilder ?? true) && hasPermission(PERMISSIONS.CONSTRUCTOR_EDITAR);
  const showHidden = adminContext || allowEditBuilder;
  const visibleSections = useMemo(() => {
    const rows = sections.flatMap((section) => {
      if (!showHidden && section.isVisible === false) return [];
      const items = (section.items ?? [])
        .filter((item) => showHidden || item.isVisible !== false)
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
      const hasActivity = items.some((item) => !isLabelItem(item));
      if (!hasActivity) return [];
      return [{ section, items }];
    });
    const currentId = adminContext
      ? undefined
      : rows.find((row) =>
          row.items.some((item) => !isLabelItem(item) && item.isCompleted !== true),
        )?.section.id;
    return rows.map((row, index) => ({
      section: row.section,
      items: row.items,
      moduleLabel: String(index + 1).padStart(2, '0'),
      state: row.section.id === currentId ? ('current' as const) : ('upcoming' as const),
    }));
  }, [sections, showHidden, adminContext]);

  const [open, setOpen] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(
      visibleSections.map((row, index) => [row.section.id, adminContext ? index === 0 : row.state === 'current']),
    ),
  );

  if (visibleSections.length === 0) {
    return (
      <div className="lms-course-details__empty">
        <span className="material-symbols-outlined lms-course-details__empty-icon" aria-hidden="true">
          menu_book
        </span>
        <p>Sin contenido</p>
        <p className="mt-2 text-sm text-dash-on-surface-variant">
          Este curso aún no tiene secciones. Añade contenido desde el constructor.
        </p>
        {allowEditBuilder && (
          <Link
            to={courseTabHref(courseId, 'edicion', searchParams)}
            className="lms-course-details__btn lms-course-details__btn--primary mt-4"
          >
            Modo edición
          </Link>
        )}
      </div>
    );
  }

  return (
    <div className="lms-course-modules flex flex-col gap-6">
      {visibleSections.map((row) => {
        const isOpen = open[row.section.id] ?? false;
        const isCurrent = row.state === 'current';
        let activityIndex = 0;

        return (
          <section
            key={row.section.id}
            className={`lms-course-module ${isCurrent ? 'lms-course-module--current' : 'lms-course-module--upcoming'}${row.section.isVisible === false ? ' opacity-75' : ''}`}
          >
            <button
              type="button"
              className="lms-course-module__head"
              onClick={() => setOpen((prev) => ({ ...prev, [row.section.id]: !isOpen }))}
              aria-expanded={isOpen}
            >
              <div>
                <div className={`lms-course-module__badge ${isCurrent ? 'lms-course-module__badge--current' : 'lms-course-module__badge--upcoming'}`}>
                  <span className="lms-course-module__dot" aria-hidden="true" />
                  <span>{isCurrent ? 'Módulo actual' : `Módulo ${row.moduleLabel}`}</span>
                </div>
                <h2 className={`lms-course-module__title${isCurrent ? ' lms-course-module__title--emphasis' : ''}`}>
                  MÓDULO {row.moduleLabel}: {row.section.title}
                </h2>
                {row.section.isVisible === false && (
                  <span className="text-xs font-medium text-dash-on-surface-variant">Sección oculta</span>
                )}
              </div>
              <span
                className={`material-symbols-outlined transition-transform ${isCurrent ? 'text-dash-primary' : 'text-dash-outline'}`}
                aria-hidden="true"
              >
                {isOpen ? 'expand_less' : 'expand_more'}
              </span>
            </button>

            {isOpen && (
              <div className="lms-course-module__body">
                <div className="lms-course-module__items">
                  {row.items.map((item) => {
                    if (isLabelItem(item)) {
                      return (
                        <p key={item.id} className="lms-course-module__label">
                          {item.title}
                        </p>
                      );
                    }

                    activityIndex += 1;
                    const href =
                      item.isAccessible !== false ? itemHref(courseId, item, adminContext, searchParams) : null;
                    const locked = item.isAccessible === false;
                    const showProgress = !adminContext;
                    const itemClassName = [
                      'lms-course-module-item',
                      'group',
                      item.isCompleted ? 'lms-course-module-item--completed' : '',
                      locked ? 'lms-course-module-item--locked' : '',
                      item.isVisible === false ? 'lms-course-module-item--disabled' : '',
                      item.hasActiveAttempt && !item.isCompleted ? 'lms-course-module-item--highlight' : '',
                    ]
                      .filter(Boolean)
                      .join(' ');
                    const content = (
                      <>
                        <div className="lms-course-module-item__main">
                          <div
                            className={`lms-course-module-item__icon${locked ? ' lms-course-module-item__icon--muted' : item.hasActiveAttempt && !item.isCompleted ? ' lms-course-module-item__icon--active' : ''}`}
                          >
                            <span className="material-symbols-outlined" aria-hidden="true">
                              {locked ? 'lock' : activityIcon(activityTypeValue(item))}
                            </span>
                          </div>
                          <div className="min-w-0 flex-1">
                            <p
                              className={`lms-course-module-item__title${locked ? ' lms-course-module-item__title--muted' : ''}`}
                            >
                              {item.title}
                            </p>
                            <p className="lms-course-module-item__meta">
                              {locked
                                ? item.accessMessage ?? 'No disponible'
                                : item.hasActiveAttempt && !item.isCompleted
                                  ? 'Continuar evaluación'
                                  : activityTypeLabel(item)}
                            </p>
                          </div>
                        </div>
                        {renderItemStatus(item, showProgress)}
                        {href && (
                          <span className="material-symbols-outlined text-dash-outline" aria-hidden="true">
                            chevron_right
                          </span>
                        )}
                      </>
                    );

                    if (href) {
                      return (
                        <Link key={item.id} to={href} className={itemClassName}>
                          {content}
                        </Link>
                      );
                    }

                    return (
                      <div
                        key={item.id}
                        className={itemClassName}
                        aria-disabled={locked || undefined}
                      >
                        {content}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}
